/**
 * Pricing — 3 variants (three_tier, single_focus, comparison)
 */
import type { PricingContent } from '../schemas/pricing';
import {
  Body,
  Btn,
  CheckIcon,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
} from './primitives';

interface PR {
  content: PricingContent;
}

function PlanCard({ plan }: { plan: PricingContent['plans'][number] }) {
  const highlighted = !!plan.highlighted;
  return (
    <div
      className="ls-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        background: highlighted ? 'var(--ls-accent-soft)' : 'var(--ls-surface)',
        border: highlighted ? '2px solid var(--ls-accent)' : '1px solid var(--ls-border)',
        position: 'relative',
      }}
    >
      {highlighted && (plan.badge ?? 'Most popular') && (
        <span
          style={{
            position: 'absolute',
            top: -12,
            left: 24,
            background: 'var(--ls-accent)',
            color: 'var(--ls-accent-text)',
            padding: '4px 12px',
            borderRadius: 999,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {plan.badge ?? 'Most popular'}
        </span>
      )}
      <H3 style={{ fontSize: '1.125rem' }}>{plan.name}</H3>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--ls-font-heading)' }}>
          {plan.price}
        </span>
        {plan.period && (
          <span style={{ color: 'var(--ls-text-muted)' }}>{plan.period}</span>
        )}
      </div>
      {plan.description && <Body style={{ fontSize: '0.9rem' }}>{plan.description}</Body>}
      <Btn variant={highlighted ? 'primary' : 'secondary'} href={plan.ctaHref ?? '#'}>
        {plan.ctaLabel}
      </Btn>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.9rem' }}>
            <span style={{ color: 'var(--ls-accent)', flexShrink: 0, marginTop: 2 }}>
              <CheckIcon size={16} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PricingThreeTier({ content }: PR) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800, alignItems: 'center', textAlign: 'center', margin: '0 auto' }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.plans.map((p, i) => (
            <PlanCard key={i} plan={p} />
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function PricingSingleFocus({ content }: PR) {
  const plan = content.plans.find((p) => p.highlighted) ?? content.plans[0];
  if (!plan) return null;
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 600, margin: '0 auto', alignItems: 'center', textAlign: 'center' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <H2>{content.headline}</H2>
        {content.subheadline && <Body>{content.subheadline}</Body>}
        <div style={{ width: '100%' }}>
          <PlanCard plan={plan} />
        </div>
      </div>
    </SectionFrame>
  );
}

export function PricingComparison({ content }: PR) {
  const allFeatures = Array.from(
    new Set(content.plans.flatMap((p) => p.features)),
  );
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ padding: 16, textAlign: 'left', color: 'var(--ls-text-muted)' }} />
                {content.plans.map((p, i) => (
                  <th key={i} style={{ padding: 16, textAlign: 'left', borderBottom: '2px solid var(--ls-border)' }}>
                    <div style={{ fontWeight: 600, fontSize: '1rem' }}>{p.name}</div>
                    <div style={{ fontSize: '1.5rem', fontFamily: 'var(--ls-font-heading)', fontWeight: 700, marginTop: 4 }}>
                      {p.price}
                      {p.period && <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--ls-text-muted)' }}>{p.period}</span>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allFeatures.map((feat, i) => (
                <tr key={i}>
                  <td style={{ padding: 16, color: 'var(--ls-text-muted)', borderBottom: '1px solid var(--ls-border)' }}>
                    {feat}
                  </td>
                  {content.plans.map((p, j) => {
                    const has = p.features.includes(feat);
                    return (
                      <td key={j} style={{ padding: 16, borderBottom: '1px solid var(--ls-border)' }}>
                        {has ? <span style={{ color: 'var(--ls-accent)' }}><CheckIcon /></span> : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td />
                {content.plans.map((p, i) => (
                  <td key={i} style={{ padding: 16 }}>
                    <Btn variant={p.highlighted ? 'primary' : 'secondary'} href={p.ctaHref ?? '#'}>
                      {p.ctaLabel}
                    </Btn>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </SectionFrame>
  );
}

export const pricingVariants = {
  three_tier: PricingThreeTier,
  single_focus: PricingSingleFocus,
  comparison: PricingComparison,
};
